import React, { useState } from "react";
import { collection, doc } from "firebase/firestore";
import { db } from "../firebase";
import PlayerForm from "../components/PlayerForm.tsx";
import { Player } from "../types.ts";
import {
  addPlayer,
  deletePlayer,
  updatePlayer,
} from "../utils/firebase-utils.js";

interface PlayersProps {
  players: Player[];
  setPlayers: (players: Player[]) => void;
}

const Players: React.FC<PlayersProps> = ({ players, setPlayers }) => {
  const [isAddingPlayer, setIsAddingPlayer] = useState<boolean>(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);

  const handleAddPlayer = (newPlayer: Omit<Player, "id"> & { id?: string }) => {
    const newDocRef = doc(collection(db, "players"));
    const uniqueId = newDocRef.id;
    const playerWithId = { ...newPlayer, id: uniqueId };
    addPlayer(playerWithId as Player);
    setPlayers([...players, playerWithId as Player]);
    setIsAddingPlayer(false);
  };

  const handleUpdatePlayer = (updatedPlayer: Player) => {
    // Formulář nezná docId, proto ho doplníme z původního záznamu –
    // jinak by se do Firebase uložily staré hodnoty.
    const existing = players.find((p) => p.id === updatedPlayer.id);
    const playerToSave: Player = {
      ...existing,
      ...updatedPlayer,
      docId: existing?.docId,
    };

    setPlayers(
      players.map((p) => (p.id === playerToSave.id ? playerToSave : p))
    );
    // updatePlayer si z předaného objektu maže docId, posíláme proto kopii
    updatePlayer({ ...playerToSave });
    setEditingPlayer(null);
  };

  const handleDeletePlayer = (playerId: string) => {
    if (window.confirm("Opravdu chcete smazat tohoto hráče?")) {
      setPlayers(players.filter((p) => p.id !== playerId));
      deletePlayer(playerId);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Hráči</h2>
        <button
          onClick={() => setIsAddingPlayer(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Přidat hráče
        </button>
      </div>

      {/* Player list */}
      {players.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jméno
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handicap [mm:ss]
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {players.map((player) => (
                <tr key={player.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {player.player_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {player.player_handicap
                        ? player.player_handicap.toFixed(2).replace(".", ":")
                        : "---"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => setEditingPlayer(player)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Upravit
                    </button>
                    <button
                      onClick={() => handleDeletePlayer(player.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Smazat
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500 text-center py-4">
          Žádní hráči nebyli nalezeni.
        </p>
      )}

      {/* Add player form */}
      {isAddingPlayer && (
        <PlayerForm
          onSubmit={handleAddPlayer}
          onCancel={() => setIsAddingPlayer(false)}
        />
      )}

      {/* Edit player form */}
      {editingPlayer && (
        <PlayerForm
          player={editingPlayer}
          onSubmit={handleUpdatePlayer}
          onCancel={() => setEditingPlayer(null)}
        />
      )}
    </div>
  );
};

export default Players;
